/**
 * TabsBar — botonera de tabs con underline animada (BO-5 bloque H).
 *
 * Usado por UserDetail (Sessions / Credits / BYOK / Activity).
 */
import type { ReactElement } from 'react';

export interface TabsBarTab<T extends string = string> {
  readonly id: T;
  readonly label: string;
  readonly badge?: number | string;
}

export interface TabsBarProps<T extends string = string> {
  tabs: ReadonlyArray<TabsBarTab<T>>;
  active: T;
  onChange: (id: T) => void;
  className?: string;
}

export function TabsBar<T extends string = string>({
  tabs,
  active,
  onChange,
  className,
}: TabsBarProps<T>): ReactElement {
  return (
    <div className={`tabs-bar ${className ?? ''}`.trim()} role="tablist">
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`tabs-bar-tab ${isActive ? 'is-active' : ''}`.trim()}
            onClick={() => {
              if (!isActive) onChange(tab.id);
            }}
            data-testid={`tab-${tab.id}`}
          >
            <span className="tabs-bar-label">{tab.label}</span>
            {tab.badge !== undefined ? (
              <span className="tabs-bar-badge">{tab.badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
